import i18n from '../i18n/config';
// frontend/src/services/soundManager.js
// Gestor centralizado de sonidos del chat (assets locales en /sounds).
// - Desbloqueo de autoplay tras la primera interacción del usuario.
// - Volumen y switches desde preferencias (chatPrefs).
// - Dedupe por evento y anti-ráfaga.
// - Multi-pestaña: solo UNA pestaña reproduce el ringtone (BroadcastChannel).
import chatPrefs from './chatPrefs';

const SOUNDS = {
  message: '/sounds/message.wav',
  mention: '/sounds/mention.wav',
  ringtone: '/sounds/call-ringtone.wav',
  callJoin: '/sounds/call-join.wav',
  callLeave: '/sounds/call-leave.wav',
  callEnd: '/sounds/call-end.wav',
  screenStart: '/sounds/screen-start.wav',
  screenStop: '/sounds/screen-stop.wav',
  error: '/sounds/error.wav',
};

const MIN_GAP_MS = 350;          // anti-ráfaga por tipo
const DEDUPE_TTL_MS = 4000;      // mismo evento (id) no suena dos veces (WS + push)

class SoundManager {
  constructor() {
    this.audios = {};
    this.unlocked = false;
    this.blocked = false;          // el navegador rechazó reproducir
    this.lastPlayed = {};          // type -> ts
    this.seenEvents = new Map();   // eventId -> ts
    this.ringAudio = null;
    this.ringingCallId = null;
    this.listeners = new Set();    // cambios de estado (para banner "Activar sonidos")
    this.tabId = Math.random().toString(36).slice(2);
    this.ringOwner = null;         // tabId dueño del ringtone

    try {
      this.bc = new BroadcastChannel(i18n.t('hardcoded.services_soundmanager.aps-chat-sound'));
      this.bc.onmessage = (e) => this._onBroadcast(e.data);
    } catch { this.bc = null; }

    // Desbloquear audio con la primera interacción
    if (typeof window !== 'undefined') {
      const unlock = () => this.unlock();
      window.addEventListener('pointerdown', unlock, { once: true, capture: true });
      window.addEventListener('keydown', unlock, { once: true, capture: true });
    }
  }

  onStateChange(cb) { this.listeners.add(cb); return () => this.listeners.delete(cb); }
  _emit() { this.listeners.forEach((cb) => { try { cb(this.getState()); } catch { /* noop */ } }); }
  getState() { return { unlocked: this.unlocked, blocked: this.blocked }; }

  _audio(type) {
    if (!this.audios[type]) {
      const a = new Audio(SOUNDS[type]);
      a.preload = 'auto';
      this.audios[type] = a;
    }
    return this.audios[type];
  }

  /** Llamar tras una interacción del usuario para desbloquear el autoplay. */
  async unlock() {
    if (this.unlocked) return true;
    try {
      const a = this._audio('message');
      a.volume = 0;
      await a.play();
      a.pause(); a.currentTime = 0;
      this.unlocked = true;
      this.blocked = false;
      this._emit();
      return true;
    } catch {
      this.blocked = true;
      this._emit();
      return false;
    }
  }

  _volume() {
    const g = chatPrefs.getGlobal();
    return Math.max(0, Math.min(1, (g.volume ?? 80) / 100));
  }

  _allowed(kind) {
    const g = chatPrefs.getGlobal();
    if (!g.sounds_enabled) return false;
    if (g.dnd_enabled) return false;
    if (chatPrefs.inQuietHours()) return false;
    if (kind === 'message' && g.sound_messages === false) return false;
    if (kind === 'mention' && g.sound_mentions === false) return false;
    if (kind === 'call' && g.sound_calls === false) return false;
    return true;
  }

  /** Dedupe por id de evento (ej. message_id) — evita WS+push dobles. */
  _dedupe(eventId) {
    if (!eventId) return false;
    const now = Date.now();
    for (const [k, ts] of this.seenEvents) { if (now - ts > DEDUPE_TTL_MS) this.seenEvents.delete(k); }
    if (this.seenEvents.has(eventId)) return true;
    this.seenEvents.set(eventId, now);
    if (this.bc) this.bc.postMessage({ t: 'seen', id: eventId });
    return false;
  }

  /**
   * Reproduce un sonido puntual.
   * type: message|mention|callJoin|callLeave|callEnd|screenStart|screenStop|error
   */
  play(type, { eventId = null, kindOverride = null } = {}) {
    const kind = kindOverride || (type === 'mention' ? 'mention' : type === 'message' ? 'message' : 'call');
    if (!this._allowed(kind)) return;
    if (this._dedupe(eventId)) return;
    const now = Date.now();
    if (now - (this.lastPlayed[type] || 0) < MIN_GAP_MS) return;
    this.lastPlayed[type] = now;
    try {
      const a = this._audio(type);
      a.volume = this._volume();
      a.currentTime = 0;
      a.play().then(() => { this.unlocked = true; this.blocked = false; this._emit(); })
        .catch(() => { this.blocked = !this.unlocked; this._emit(); });
    } catch { /* noop */ }
  }

  /** Inicia el tono de llamada en loop (solo una pestaña lo reproduce). */
  startRing(callId) {
    if (!this._allowed('call')) return;
    if (this.ringingCallId === callId) return;
    // Reclamar el ring: si otra pestaña ya lo tiene, no sonar aquí
    if (this.ringOwner && this.ringOwner !== this.tabId) return;
    this.ringOwner = this.tabId;
    if (this.bc) this.bc.postMessage({ t: 'ring-claim', tab: this.tabId, callId });
    this.ringingCallId = callId;
    try {
      if (!this.ringAudio) {
        this.ringAudio = new Audio(SOUNDS.ringtone);
        this.ringAudio.loop = true;
      }
      this.ringAudio.volume = this._volume();
      this.ringAudio.currentTime = 0;
      this.ringAudio.play().catch(() => { this.blocked = !this.unlocked; this._emit(); });
    } catch { /* noop */ }
  }

  stopRing(callId = null) {
    if (callId && this.ringingCallId && callId !== this.ringingCallId) return;
    this.ringingCallId = null;
    if (this.ringOwner === this.tabId) this.ringOwner = null;
    try { this.ringAudio?.pause(); if (this.ringAudio) this.ringAudio.currentTime = 0; } catch { /* noop */ }
    if (this.bc) this.bc.postMessage({ t: 'ring-stop', callId });
  }

  _onBroadcast(msg) {
    if (!msg) return;
    if (msg.t === 'seen' && msg.id) this.seenEvents.set(msg.id, Date.now());
    if (msg.t === 'ring-claim' && msg.tab !== this.tabId) {
      // Otra pestaña reclama el ring primero → esta no suena
      if (!this.ringingCallId) this.ringOwner = msg.tab;
    }
    if (msg.t === 'ring-stop') {
      this.ringOwner = null;
      if (this.ringingCallId === msg.callId || !msg.callId) {
        this.ringingCallId = null;
        try { this.ringAudio?.pause(); } catch { /* noop */ }
      }
    }
  }

  /** Limpieza al logout. */
  reset() {
    this.stopRing();
    this.seenEvents.clear();
  }
}

const soundManager = new SoundManager();
export default soundManager;
